'use client';

import React, { useState } from 'react';
import { Search, Filter } from 'lucide-react';
import type { EditorPost } from './ContentEditorLayout';

interface Props {
  posts: EditorPost[];
  selectedId: string;
  onSelect: (id: string) => void;
}

const platformBadge: Record<string, string> = {
  LI: 'platform-badge-li',
  IG: 'platform-badge-ig',
  X: 'platform-badge-x',
  FB: 'platform-badge-fb',
  BLOG: 'platform-badge-blog',
};

const statusStyle: Record<string, string> = {
  'Freigegeben': 'text-green-400',
  'Entwurf': 'text-muted',
  'KI-generiert': 'text-accent',
  'Bearbeitet': 'text-blue-400',
  'Geplant': 'text-purple-400',
};

export default function PostList({ posts, selectedId, onSelect }: Props) {
  const [search, setSearch] = useState('');
  const [filterPlatform, setFilterPlatform] = useState<string>('alle');

  const platforms = ['alle', ...Array.from(new Set(posts.map((p) => p.platform)))];

  const filtered = posts.filter((p) => {
    const matchSearch = p.title.toLowerCase().includes(search.toLowerCase());
    const matchPlatform = filterPlatform === 'alle' || p.platform === filterPlatform;
    return matchSearch && matchPlatform;
  });

  return (
    <div className="w-72 flex-shrink-0 border-r border-border flex flex-col bg-black">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <p className="section-label">Beiträge</p>
          <span className="mono text-xs text-muted">{posts.length} gesamt</span>
        </div>
        <div className="relative mb-3">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="text"
            className="input-punk pl-8 py-2 text-xs"
            placeholder="Suchen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {platforms.map((plat) => (
            <button
              key={`filter-${plat}`}
              onClick={() => setFilterPlatform(plat)}
              className={`text-xs mono px-2 py-1 rounded border transition-all ${
                filterPlatform === plat
                  ? 'border-accent text-accent bg-accent/8' :'border-border text-muted hover:border-accent/40 hover:text-white'
              }`}
            >
              {plat}
            </button>
          ))}
        </div>
      </div>

      {/* Post list */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-6 text-center">
            <Filter size={20} className="text-muted mx-auto mb-2" />
            <p className="text-xs text-muted">Keine Beiträge gefunden</p>
          </div>
        ) : (
          filtered.map((post) => (
            <button
              key={post.id}
              onClick={() => onSelect(post.id)}
              className={`w-full text-left p-4 border-b border-border/50 transition-all hover:bg-surface group ${
                selectedId === post.id ? 'bg-accent/5 border-l-2 border-l-accent' : ''
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className={`status-badge ${platformBadge[post.platform] ?? ''}`}>
                  {post.platform}
                </span>
                <span className="text-xs text-muted">{post.format}</span>
                <span className={`ml-auto text-xs mono ${statusStyle[post.status] ?? 'text-muted'}`}>
                  ●
                </span>
              </div>
              <p className="text-xs font-500 text-white leading-snug line-clamp-2">{post.title}</p>
              <div className="flex items-center gap-3 mt-2">
                <span className="mono text-xs text-muted">Tag {post.day}</span>
                <span className="mono text-xs text-muted">{post.words} W</span>
                <span className={`mono text-xs ml-auto ${statusStyle[post.status] ?? 'text-muted'}`}>
                  {post.status}
                </span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}